import React, { useEffect, useState, useRef } from "react";
import Player from "./Player";
import soundManager from "audio/OrtivisSounds";

const ASPECT_RATIO = 1077 / 606;
const REM_TO_PX = 16;
const CELL_WIDTH = 3 * REM_TO_PX;
const GAP_WIDTH = 2.2 * REM_TO_PX;
const CONTAINER_PADDING = 1.5 * 2 * REM_TO_PX;

const BoardPath = ({
	boardCases,
	players,
	currentPlayerIndex,
	playerRefs,
	screenHeight,
}) => {
	const [worldWidth, setWorldWidth] = useState(0);
	const [totalBoardWidth, setTotalBoardWidth] = useState(0);
	const prevPlayerPositions = useRef(players.map((player) => player.position)); // Store previous positions

	useEffect(() => {
		const calculatedWorldWidth = Math.round(screenHeight * ASPECT_RATIO);
		setWorldWidth(calculatedWorldWidth);
	}, [screenHeight]);

	useEffect(() => {
		if (worldWidth > 0) {
			const estimatedPathLength =
				boardCases.length * (CELL_WIDTH + GAP_WIDTH) + CONTAINER_PADDING;
			const requiredWorlds = Math.ceil(estimatedPathLength / worldWidth);
			const totalBoardWidth = requiredWorlds * worldWidth;
			setTotalBoardWidth(totalBoardWidth);
		}
	}, [boardCases.length, worldWidth]);

	// Compute spacing for each board case
	const caseSpacing =
		(totalBoardWidth - CONTAINER_PADDING) / (boardCases.length || 1); // Prevent division by zero px

	useEffect(() => {
		// Check if any player has actually moved
		const hasMoved = players.some(
			(player, index) => player.position !== prevPlayerPositions.current[index]
		);

		if (hasMoved) {
			soundManager.playSound("pawnMove");
		}

		// Update previous positions after the check
		prevPlayerPositions.current = players.map((player) => player.position);
	}, [players]);

	return (
		<div
			className="absolute left-0 bottom-[14%] px-6 flex w-full"
			style={{ gap: Math.ceil(caseSpacing - CELL_WIDTH) + "px" }}
		>
			{boardCases.map((boardCase, index) => {
				// Get players on the same position
				const playersOnCase = players.filter(
					(player) => player.position === index
				);

				const variation = Math.sin(index * 1.3);
				const variation2 = Math.sin(index * 4.7);
				const width = 3;
				const height = 2 + (variation + variation2) * 1;

				return (
					<div
						key={index}
						className="relative flex flex-col items-center justify-center 
						bg-gradient-to-br from-[#ba8d68] to-[#bba27f] border-[#8c6239] rounded-lg 
						shadow-[3px_6px_10px_rgba(0,0,0,0.1)] transition-transform duration-300 ease-in-out"
						style={{
							transform: `perspective(6000px) rotateX(10deg) skewX(35deg) translateY(${Math.abs(
								variation2 / 3
							)}rem)`,
							transformOrigin: "center bottom",
							width: `${width}rem`,
							height: `${height}rem`,
							minWidth: "3rem",
							maxWidth: "5rem",
							minHeight: "1.5rem",
							maxHeight: "2.5rem",
						}}
					>
						<span className="text-amber-400">{index}</span>
						{playersOnCase.length > 0 && (
							<div
								className="absolute right-0 h-5 w-full -left-4 flex gap-4 flex-wrap justify-start items-end text-white"
								style={{
									transform: `perspective(6000px) skewX(-35deg) rotateX(-10deg)`,
									transformOrigin: "center bottom",
								}}
							>
								{playersOnCase.map((player, playerIndex) => (
									<div
										key={playerIndex}
										className="relative h-3 w-3"
										style={{
											zIndex: playerIndex + 100,
										}}
									>
										<Player
											player={player}
											isCurrent={currentPlayerIndex === players.indexOf(player)}
											ref={(el) =>
												(playerRefs.current[players.indexOf(player)] = el)
											}
										/>
									</div>
								))}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

export default BoardPath;
